import { NoteLevelPicker } from '@/components/notes/note-level-picker';
import { noteErrorMessage, visibleByPhrase } from '@/lib/note-format';
import { cn } from '@/lib/utils';
import {
  DEFAULT_NOTE_VISIBILITY,
  NOTE_BODY_MAX,
  type NoteVisibility,
  type UserRole,
} from '@mss_creator/shared';
import { Button, TextArea } from '@pms-digital/design-system';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';

/** The counter appears once the text gets this close to the limit. */
const COUNTER_FROM = 1800;

export interface NoteComposerSubmit {
  body: string;
  /** The level picked; meaningless for a reply, which takes the level of its root. */
  visibility: NoteVisibility;
}

export interface NoteComposerProps {
  /** `note` starts a discussion (level picker and « Visible par »); `reply` answers one. */
  mode: 'note' | 'reply';
  /** The role in force: a client role is not offered « Équipe PMS ». */
  role: UserRole;
  /** Named in « Visible par <entreprise> ». */
  companyName: string;
  /**
   * Publishes. Resolve once the server answered (the field then empties); reject
   * to show the failure under the field and keep the text.
   */
  onSubmit: (input: NoteComposerSubmit) => Promise<unknown>;
  /** Escape and « Annuler » call this; without it neither is offered. */
  onCancel?: () => void;
  /** Focuses the field when it mounts and again each time this value changes. */
  focusKey?: number;
  className?: string;
}

/**
 * The field that writes a note or a reply (planche 11): the TextArea of the
 * design system (it no longer grows with its content), Client
 * by default for every role with the level in plain sight and the sentence
 * « Visible par … » under it, a counter near the 2000-character limit,
 * Ctrl/Cmd+Enter publishes, Escape cancels.
 */
export function NoteComposer({
  mode,
  role,
  companyName,
  onSubmit,
  onCancel,
  focusKey,
  className,
}: NoteComposerProps) {
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<NoteVisibility>(DEFAULT_NOTE_VISIBILITY);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (focusKey !== undefined) textareaRef.current?.focus();
  }, [focusKey]);

  const trimmed = body.trim();
  const overLimit = body.length > NOTE_BODY_MAX;
  const canSubmit = trimmed.length > 0 && !overLimit && !pending;
  const placeholder = mode === 'reply' ? 'Répondre…' : 'Ajouter une note…';

  async function submit() {
    if (!canSubmit) return;
    setPending(true);
    setError(null);
    try {
      await onSubmit({ body: trimmed, visibility });
      setBody('');
      setVisibility(DEFAULT_NOTE_VISIBILITY);
    } catch (failure) {
      setError(
        noteErrorMessage(
          failure,
          mode === 'reply'
            ? "La réponse n'a pas pu être publiée. Réessayez."
            : "La note n'a pas pu être publiée. Réessayez.",
        ),
      );
    } finally {
      setPending(false);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void submit();
    } else if (event.key === 'Escape' && onCancel) {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <TextArea
        ref={textareaRef}
        label={<span>{placeholder}</span>}
        rows={2}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        status={overLimit ? 'error' : 'default'}
        readOnly={pending}
        {...(mode === 'note'
          ? {
              help: `${visibleByPhrase(visibility, companyName)}. Ctrl+Entrée publie${onCancel ? ', Échap ferme' : ''}.`,
            }
          : {})}
      />
      <div className="flex items-center gap-2">
        {mode === 'note' && (
          <NoteLevelPicker
            value={visibility}
            onChange={setVisibility}
            role={role}
            companyName={companyName}
          />
        )}
        {body.length >= COUNTER_FROM && (
          <span
            aria-live="polite"
            className={cn(
              'text-micro',
              overLimit ? 'font-semibold text-rouge-deep' : 'text-gray-600',
            )}
          >
            {body.length} / {NOTE_BODY_MAX}
          </span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {onCancel && (
            <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={pending}>
              Annuler
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!canSubmit}
            loading={pending}
            loadingLabel="Publication…"
            onClick={() => void submit()}
          >
            Publier
          </Button>
        </div>
      </div>
      {error && (
        <p role="alert" className="font-semibold text-caption text-rouge-deep">
          {error}
        </p>
      )}
    </div>
  );
}
